import { useCallback, useEffect, useMemo, useRef, useState } from "react";

const API_ROOT = "https://api.briolabs.io";
const POLL_INTERVAL_MS = 2500;
const ZONE_SCOPE_LIMITATION_MARKER = "zone_id is not available";
const ZONE_SCOPE_UNAVAILABLE_MESSAGE = "此場域目前僅支援「場域級叫號」，尚未啟用分區叫號。";
const ZONE_SCOPE_RECOVERY_MESSAGE = "請切換至「全部區域／場域」後再操作。";

const t = {
  bg: "#0F1117",
  panel: "#1A1D27",
  panel2: "#141720",
  border: "#2A2E3A",
  accent: "#6C5CE7",
  success: "#00B894",
  successSoft: "rgba(0,184,148,0.14)",
  warning: "#FDCB6E",
  warningSoft: "rgba(253,203,110,0.14)",
  danger: "#E17055",
  dangerSoft: "rgba(225,112,85,0.14)",
  text: "#E8E8ED",
  muted: "#8B8FA3",
  dim: "#5A5E72",
  mono: "'JetBrains Mono', 'SF Mono', monospace",
};

async function api(path, options = {}) {
  const token = localStorage.getItem("biz_token");
  const response = await fetch(`${API_ROOT}${path}`, {
    ...options,
    headers: {
      "Content-Type": "application/json",
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...options.headers,
    },
    body: options.body === undefined ? undefined : JSON.stringify(options.body),
  });

  let body = null;
  try {
    body = await response.json();
  } catch (_) {
    body = null;
  }

  if (!response.ok) {
    const error = new Error(
      body?.message
      || body?.error
      || body?.code
      || (response.status === 503 ? "服務暫時無法使用" : `操作失敗 (${response.status})`)
    );
    error.status = response.status;
    error.code = body?.code || body?.error || null;
    error.body = body;
    throw error;
  }

  return body && typeof body === "object" ? body : {};
}

function unwrapList(json, key) {
  const data = json?.data !== undefined ? json.data : json;
  if (Array.isArray(data)) return data;
  if (Array.isArray(data?.[key])) return data[key];
  if (Array.isArray(data?.items)) return data.items;
  return [];
}

function sameSelection(selection, venueId, zoneId) {
  return selection?.venueId === venueId && selection?.zoneId === zoneId;
}

function hasZoneScopeLimitation(limitations) {
  return Array.isArray(limitations) && limitations.some(
    (item) => typeof item === "string" && item.includes(ZONE_SCOPE_LIMITATION_MARKER)
  );
}

function safeIdentity(value) {
  return typeof value === "string" || typeof value === "number" ? value : null;
}

function validateQueueStateScope(state, expectedVenueId, expectedZoneId = null) {
  const isObject = !!state && typeof state === "object" && !Array.isArray(state);
  const venueMatches = isObject && state.venue_id === expectedVenueId;
  const zoneMatches = expectedZoneId === null || (isObject && state.zone_id === expectedZoneId);
  if (venueMatches && zoneMatches) return state;

  const error = new Error("QUEUE_STATE_SCOPE_MISMATCH");
  error.code = "QUEUE_STATE_SCOPE_MISMATCH";
  error.scopeIdentity = {
    expected_venue_id: expectedVenueId,
    expected_zone_id: expectedZoneId,
    received_venue_id: safeIdentity(isObject ? state.venue_id : null),
    received_zone_id: safeIdentity(isObject ? state.zone_id : null),
  };
  throw error;
}

function createIdempotencyKey() {
  const cryptoApi = typeof window !== "undefined" ? window.crypto : null;
  if (typeof cryptoApi?.randomUUID === "function") {
    return cryptoApi.randomUUID();
  }
  if (typeof cryptoApi?.getRandomValues === "function") {
    const bytes = new Uint8Array(16);
    cryptoApi.getRandomValues(bytes);
    return Array.from(bytes, (value) => value.toString(16).padStart(2, "0")).join("");
  }
  return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2)}-${Math.random().toString(36).slice(2)}`;
}

function fmtTime(value) {
  if (!value) return "—";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "—";
  return date.toLocaleString("zh-TW", {
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  });
}

function fmtRefreshTime(value) {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  return [date.getHours(), date.getMinutes(), date.getSeconds()]
    .map((part) => String(part).padStart(2, "0"))
    .join(":");
}

function elapsedLabel(value, verb) {
  if (!value) return null;
  const startedAt = new Date(value).getTime();
  if (Number.isNaN(startedAt)) return null;
  const elapsedMinutes = Math.max(0, Math.floor((Date.now() - startedAt) / 60000));
  if (elapsedMinutes < 1) return `${verb}未滿 1 分鐘`;
  if (elapsedMinutes < 60) return `已${verb} ${elapsedMinutes} 分鐘`;
  const hours = Math.floor(elapsedMinutes / 60);
  const minutes = elapsedMinutes % 60;
  return `已${verb} ${hours} 小時${minutes ? ` ${minutes} 分鐘` : ""}`;
}

function fmtWaitingDuration(ticket) {
  return elapsedLabel(ticket.sort_timestamp, "等待") || "等待時間 —";
}

function fmtHeldDuration(ticket) {
  return elapsedLabel(
    ticket.held_at || ticket.updated_at || ticket.sort_timestamp || ticket.called_at,
    "保留"
  );
}

function sourceLabel(ticket) {
  if (ticket.source === "line" || ticket.queue_source === "line") return "LINE";
  if (ticket.source === "staff" || ticket.queue_source === "manual") return "櫃台";
  return "現場";
}

const statusLabel = {
  waiting: "等候中",
  called: "已叫號",
  serving: "服務中",
  held: "保留",
  skipped: "過號",
  completed: "完成",
  cancelled: "取消",
};

const eventLabel = {
  CREATE_TICKET: "建立號碼",
  CALL_NEXT: "叫下一號",
  RECALL: "再次叫號",
  HOLD: "保留",
  SKIP: "過號",
  COMPLETE: "完成",
  ticket_called_from_waiting: "指定叫號",
  ticket_cancelled_from_waiting: "取消等待",
  ticket_called_from_hold: "重叫保留號碼",
  ticket_resumed_from_hold: "恢復等待",
  ticket_cancelled_from_hold: "取消保留",
};

const calledEventActions = new Set([
  "CALL_NEXT",
  "ticket_called_from_waiting",
  "ticket_called_from_hold",
  "RECALL",
]);

function currentCalledTime(current, events = []) {
  if (!current?.id) return "—";
  const matching = events
    .filter((event) => event.ticket_id === current.id && calledEventActions.has(event.action))
    .map((event) => ({ value: event.created_at, time: new Date(event.created_at).getTime() }))
    .filter((event) => !Number.isNaN(event.time))
    .sort((a, b) => b.time - a.time);
  return matching.length ? fmtTime(matching[0].value) : "—";
}

function buttonStyle(kind = "secondary", disabled = false) {
  const map = {
    primary: { bg: t.accent, fg: "#fff", border: t.accent },
    success: { bg: t.success, fg: "#061B17", border: t.success },
    warning: { bg: t.warning, fg: "#1E1600", border: t.warning },
    danger: { bg: t.dangerSoft, fg: t.danger, border: "rgba(225,112,85,0.4)" },
    secondary: { bg: t.panel2, fg: t.text, border: t.border },
  };
  const color = map[kind] || map.secondary;
  return {
    padding: "10px 14px",
    borderRadius: 8,
    border: `1px solid ${color.border}`,
    background: disabled ? t.border : color.bg,
    color: disabled ? t.dim : color.fg,
    fontSize: 13,
    fontWeight: 700,
    cursor: disabled ? "not-allowed" : "pointer",
    whiteSpace: "nowrap",
  };
}

const inputStyle = {
  width: "100%",
  minWidth: 0,
  boxSizing: "border-box",
  padding: "11px 12px",
  borderRadius: 8,
  border: `1px solid ${t.border}`,
  background: t.panel2,
  color: t.text,
  fontSize: 14,
  outline: "none",
};

function Field({ label, children }) {
  return (
    <label style={{ display: "block", minWidth: 0 }}>
      <div style={{ fontSize: 12, color: t.muted, marginBottom: 6, fontWeight: 700 }}>{label}</div>
      {children}
    </label>
  );
}

function Panel({ children, style }) {
  return (
    <div style={{ background: t.panel, border: `1px solid ${t.border}`, borderRadius: 10, padding: 16, minWidth: 0, ...style }}>
      {children}
    </div>
  );
}

function WaitingTicketRow({ ticket, disabled, callDisabled, onAction }) {
  const displayNumber = ticket.display_number || ticket.ticket_number || "—";
  return (
    <div style={{ padding: 12, borderRadius: 8, background: t.panel2, border: `1px solid ${t.border}`, marginBottom: 8 }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 8, marginBottom: 10 }}>
        <div style={{ fontSize: 18, fontWeight: 800, color: t.text }}>{displayNumber}</div>
        <div style={{ fontSize: 12, color: t.muted }}>{fmtWaitingDuration(ticket)} · {sourceLabel(ticket)}</div>
      </div>
      <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
        <button
          aria-label={`指定叫號 ${displayNumber}`}
          onClick={() => onAction("call-waiting", ticket, "已指定叫號")}
          disabled={disabled || callDisabled}
          style={buttonStyle("success", disabled || callDisabled)}
        >
          指定叫號
        </button>
        <button
          aria-label={`取消等待 ${displayNumber}`}
          onClick={() => onAction("cancel-waiting", ticket, "等待號碼已取消")}
          disabled={disabled}
          style={buttonStyle("danger", disabled)}
        >
          取消等待
        </button>
      </div>
    </div>
  );
}

function HeldTicketRow({ ticket, disabled, callDisabled, onAction }) {
  const displayNumber = ticket.display_number || ticket.ticket_number || "—";
  const duration = fmtHeldDuration(ticket);
  return (
    <div style={{ padding: 12, borderRadius: 8, background: t.panel2, border: `1px solid ${t.border}`, marginBottom: 8 }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 8, marginBottom: 10 }}>
        <div style={{ fontSize: 18, fontWeight: 800, color: t.text }}>{displayNumber}</div>
        {duration && <div style={{ fontSize: 12, color: t.muted }}>{duration}</div>}
      </div>
      <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
        <button
          aria-label={`重叫 ${displayNumber}`}
          onClick={() => onAction("call-held", ticket, "已重叫保留號碼")}
          disabled={disabled || callDisabled}
          style={buttonStyle("success", disabled || callDisabled)}
        >
          重叫
        </button>
        <button
          aria-label={`恢復等候 ${displayNumber}`}
          onClick={() => onAction("resume-held", ticket, "已恢復至等候佇列")}
          disabled={disabled}
          style={buttonStyle("secondary", disabled)}
        >
          恢復等候
        </button>
        <button
          aria-label={`取消保留 ${displayNumber}`}
          onClick={() => onAction("cancel-held", ticket, "保留號碼已取消")}
          disabled={disabled}
          style={buttonStyle("danger", disabled)}
        >
          取消
        </button>
      </div>
    </div>
  );
}

export default function QueueConsolePage({ onAuthExpired = () => {} }) {
  const [venues, setVenues] = useState([]);
  const [zones, setZones] = useState([]);
  const [venueId, setVenueId] = useState("");
  const [zoneId, setZoneId] = useState("");
  const [venueState, setVenueState] = useState(null);
  const [scopedState, setScopedState] = useState(null);
  const [loading, setLoading] = useState(true);
  const [commandBusy, setCommandBusy] = useState(false);
  const [manualRefreshBusy, setManualRefreshBusy] = useState(false);
  const [lastRefreshedAt, setLastRefreshedAt] = useState(null);
  const [manualRefreshError, setManualRefreshError] = useState(null);
  const [scopeLocked, setScopeLocked] = useState(false);
  const [stateReady, setStateReady] = useState(false);
  const [authExpired, setAuthExpired] = useState(false);
  const [displayNumber, setDisplayNumber] = useState("");
  const [notice, setNotice] = useState(null);
  const [syncWarning, setSyncWarning] = useState(null);
  const [zoneScopeUnavailableSelection, setZoneScopeUnavailableSelection] = useState(null);

  const mountedRef = useRef(false);
  const selectionRef = useRef({ venueId: "", zoneId: "" });
  const commandLockRef = useRef(false);
  const manualRefreshBusyRef = useRef(false);
  const authExpiredRef = useRef(false);
  const scopeLockSelectionRef = useRef(null);
  const authExpiredCallbackRef = useRef(onAuthExpired);
  const stateGenerationRef = useRef(0);
  const stateControllersRef = useRef(new Set());
  const pollInFlightRef = useRef(false);
  const loadedSelectionRef = useRef(null);

  useEffect(() => {
    authExpiredCallbackRef.current = onAuthExpired;
  }, [onAuthExpired]);

  useEffect(() => {
    selectionRef.current = { venueId, zoneId };
  }, [venueId, zoneId]);

  const selectedVenue = useMemo(
    () => venues.find((venue) => venue.id === venueId) || null,
    [venues, venueId]
  );
  const current = venueState?.current_ticket || null;
  const scopedStateHasZoneLimitation = hasZoneScopeLimitation(scopedState?.limitations);
  const zoneScopeUnavailable = !!zoneId && (
    scopedStateHasZoneLimitation
    || sameSelection(zoneScopeUnavailableSelection, venueId, zoneId)
  );
  const currentOwnedByScope = !!current && (!zoneId || current.zone_id === zoneId);
  const currentOwnedByOtherZone = (
    !!current
    && !!zoneId
    && !zoneScopeUnavailable
    && current.zone_id !== zoneId
  );
  const venueScopeCurrent = (
    zoneScopeUnavailable
    && !!current
    && !current.zone_id
  );
  const mutationDisabled = (
    commandBusy
    || scopeLocked
    || zoneScopeUnavailable
    || authExpired
    || !venueId
    || !stateReady
  );
  const calledAt = currentCalledTime(current, venueState?.recent_events);

  const currentZoneLabel = useMemo(() => {
    if (!current?.zone_id) return "其他區域";
    const zone = zones.find((item) => item.id === current.zone_id);
    return zone?.name || zone?.id || current.zone_id;
  }, [current, zones]);

  const expireAuth = useCallback(() => {
    if (authExpiredRef.current) return;
    authExpiredRef.current = true;
    setAuthExpired(true);
    stateGenerationRef.current += 1;
    stateControllersRef.current.forEach((controller) => controller.abort());
    stateControllersRef.current.clear();
    commandLockRef.current = false;
    manualRefreshBusyRef.current = false;
    setCommandBusy(false);
    setManualRefreshBusy(false);
    setLastRefreshedAt(null);
    setManualRefreshError(null);
    authExpiredCallbackRef.current();
  }, []);

  const invalidateStateRequests = useCallback(() => {
    stateGenerationRef.current += 1;
    stateControllersRef.current.forEach((controller) => controller.abort());
    stateControllersRef.current.clear();
    pollInFlightRef.current = false;
  }, []);

  const markStateUnready = useCallback(() => {
    loadedSelectionRef.current = null;
    setStateReady(false);
  }, []);

  const lockStateScopeMismatch = useCallback((error, requestedVenueId, requestedZoneId) => {
    if (
      !mountedRef.current
      || !sameSelection(selectionRef.current, requestedVenueId, requestedZoneId)
    ) {
      return;
    }
    markStateUnready();
    scopeLockSelectionRef.current = {
      venueId: requestedVenueId,
      zoneId: requestedZoneId,
    };
    setScopeLocked(true);
    setNotice({
      kind: "error",
      code: "QUEUE_STATE_SCOPE_MISMATCH",
      msg: "系統回傳的場域範圍不一致，操作已停用",
    });
    console.warn(
      "[QueueOperations] QUEUE_STATE_SCOPE_MISMATCH",
      error.scopeIdentity
    );
  }, [markStateUnready]);

  const handleStateLoadError = useCallback((error, mode, requestedVenueId, requestedZoneId) => {
    if (error?.name === "AbortError" || !mountedRef.current) return;
    if (error?.code === "QUEUE_STATE_SCOPE_MISMATCH") {
      lockStateScopeMismatch(error, requestedVenueId, requestedZoneId);
      return;
    }
    if (!sameSelection(loadedSelectionRef.current, requestedVenueId, requestedZoneId)) {
      setStateReady(false);
    }
    if (error?.status === 401) {
      expireAuth();
      return;
    }
    if (mode === "poll" || mode === "command-recovery") return;
    if (error?.status === 404 && error?.code === "VENUE_NOT_FOUND") {
      scopeLockSelectionRef.current = {
        venueId: requestedVenueId,
        zoneId: requestedZoneId,
      };
      setScopeLocked(true);
      setNotice({ kind: "error", msg: "無法存取此場域" });
      console.warn("[QueueOperations] venue access denied", {
        status: error.status,
        code: error.code,
        venue_id: requestedVenueId,
      });
      return;
    }
    if (mode === "manual") return;
    setNotice({
      kind: "error",
      msg: error?.status === 503 ? "服務暫時無法使用，請稍後再試" : (error?.message || "無法載入佇列"),
    });
  }, [expireAuth, lockStateScopeMismatch]);

  const loadAuthoritativeState = useCallback(async ({
    requestedVenueId = selectionRef.current.venueId,
    requestedZoneId = selectionRef.current.zoneId,
    mode = "manual",
    venueOnly = false,
  } = {}) => {
    if (!requestedVenueId || authExpiredRef.current) return null;

    const generation = stateGenerationRef.current + 1;
    stateGenerationRef.current = generation;
    const controller = new AbortController();
    stateControllersRef.current.add(controller);
    if (mode === "initial") setLoading(true);

    const statePath = (scopeZoneId = "") => {
      const query = new URLSearchParams({ venue_id: requestedVenueId });
      if (scopeZoneId) query.set("zone_id", scopeZoneId);
      return `/api/v1/queue-console/state?${query.toString()}`;
    };

    try {
      let nextVenueState;
      let nextScopedState;
      if (venueOnly) {
        const json = await api(statePath(), { signal: controller.signal });
        nextVenueState = json.state || json;
      } else if (requestedZoneId) {
        const [venueJson, scopedJson] = await Promise.all([
          api(statePath(), { signal: controller.signal }),
          api(statePath(requestedZoneId), { signal: controller.signal }),
        ]);
        nextVenueState = venueJson.state || venueJson;
        nextScopedState = scopedJson.state || scopedJson;
      } else {
        const json = await api(statePath(), { signal: controller.signal });
        nextVenueState = json.state || json;
        nextScopedState = nextVenueState;
      }

      const active = selectionRef.current;
      if (
        !mountedRef.current
        || authExpiredRef.current
        || generation !== stateGenerationRef.current
        || active.venueId !== requestedVenueId
        || active.zoneId !== requestedZoneId
      ) {
        return null;
      }

      validateQueueStateScope(nextVenueState, requestedVenueId);
      if (!venueOnly && requestedZoneId) {
        validateQueueStateScope(nextScopedState, requestedVenueId, requestedZoneId);
      }

      setVenueState(nextVenueState);
      if (!venueOnly) {
        setScopedState(nextScopedState);
        loadedSelectionRef.current = {
          venueId: requestedVenueId,
          zoneId: requestedZoneId,
        };
        setStateReady(true);
      } else if (sameSelection(loadedSelectionRef.current, requestedVenueId, requestedZoneId)) {
        setStateReady(true);
      }
      const lockedSelection = scopeLockSelectionRef.current;
      if (
        lockedSelection
        && (
          lockedSelection.venueId !== requestedVenueId
          || lockedSelection.zoneId !== requestedZoneId
        )
      ) {
        scopeLockSelectionRef.current = null;
        setScopeLocked(false);
      }
      setLoading(false);
      return { venueState: nextVenueState, scopedState: nextScopedState };
    } catch (error) {
      handleStateLoadError(error, mode, requestedVenueId, requestedZoneId);
      throw error;
    } finally {
      stateControllersRef.current.delete(controller);
      if (
        mode === "initial"
        && mountedRef.current
        && generation === stateGenerationRef.current
        && sameSelection(selectionRef.current, requestedVenueId, requestedZoneId)
      ) {
        setLoading(false);
      }
    }
  }, [handleStateLoadError]);

  const loadZones = useCallback(async (requestedVenueId) => {
    if (!requestedVenueId || authExpiredRef.current) {
      setZones([]);
      return;
    }
    try {
      const json = await api(`/api/v1/provisioning/venues/${encodeURIComponent(requestedVenueId)}/zones`);
      if (mountedRef.current && selectionRef.current.venueId === requestedVenueId && !authExpiredRef.current) {
        setZones(unwrapList(json, "zones"));
      }
    } catch (error) {
      if (error?.status === 401) expireAuth();
      else if (mountedRef.current && selectionRef.current.venueId === requestedVenueId) setZones([]);
    }
  }, [expireAuth]);

  useEffect(() => {
    mountedRef.current = true;
    let active = true;
    setLoading(true);
    api("/api/v1/provisioning/venues")
      .then((json) => {
        if (!active || authExpiredRef.current) return;
        const list = unwrapList(json, "venues");
        setVenues(list);
        setVenueId((previous) => previous || list[0]?.id || "");
      })
      .catch((error) => {
        if (!active || error?.name === "AbortError") return;
        if (error?.status === 401) expireAuth();
        else setNotice({ kind: "error", msg: error?.message || "無法載入場域" });
      })
      .finally(() => {
        if (active) setLoading(false);
      });

    return () => {
      active = false;
      mountedRef.current = false;
      invalidateStateRequests();
      manualRefreshBusyRef.current = false;
    };
  }, [expireAuth, invalidateStateRequests]);

  useEffect(() => {
    if (!venueId || authExpired) return;
    loadZones(venueId);
  }, [authExpired, loadZones, venueId]);

  useEffect(() => {
    if (!venueId || authExpired) {
      if (!venueId) {
        markStateUnready();
        setVenueState(null);
        setScopedState(null);
      }
      return;
    }
    loadAuthoritativeState({
      requestedVenueId: venueId,
      requestedZoneId: zoneId,
      mode: "initial",
    }).catch(() => {});
  }, [authExpired, loadAuthoritativeState, markStateUnready, venueId, zoneId]);

  useEffect(() => {
    if (!venueId || authExpired) return undefined;
    const poll = () => {
      if (
        document.visibilityState !== "visible"
        || commandLockRef.current
        || manualRefreshBusyRef.current
        || authExpiredRef.current
        || pollInFlightRef.current
        || stateControllersRef.current.size > 0
      ) return;
      pollInFlightRef.current = true;
      const active = selectionRef.current;
      loadAuthoritativeState({
        requestedVenueId: active.venueId,
        requestedZoneId: active.zoneId,
        mode: "poll",
      })
        .catch(() => {})
        .finally(() => {
          pollInFlightRef.current = false;
        });
    };
    const timer = window.setInterval(poll, POLL_INTERVAL_MS);
    const onVisibilityChange = () => {
      if (document.visibilityState === "visible") poll();
    };
    document.addEventListener("visibilitychange", onVisibilityChange);
    return () => {
      window.clearInterval(timer);
      document.removeEventListener("visibilitychange", onVisibilityChange);
      pollInFlightRef.current = false;
    };
  }, [authExpired, loadAuthoritativeState, venueId, zoneId]);

  const queueErrorPolicy = useCallback((error, requestedVenueId, requestedZoneId) => {
    if (error?.status === 401) {
      expireAuth();
      return { recover: false };
    }

    const messages = {
      CURRENT_TICKET_EXISTS: "本場域目前已有叫號中的號碼",
      TICKET_NOT_WAITING: "此號碼狀態已由其他操作員變更",
      TICKET_STATE_CONFLICT: "另一位操作員已先完成此操作",
      QUEUE_EMPTY: "目前沒有等待號碼",
      NO_CURRENT_TICKET: "目前沒有可操作的叫號",
    };
    if (messages[error?.code]) {
      setNotice({ kind: "error", msg: messages[error.code] });
      return { recover: true };
    }

    if (error?.code === "QUEUE_ZONE_SCOPE_UNAVAILABLE") {
      if (requestedZoneId) {
        setZoneScopeUnavailableSelection({
          venueId: requestedVenueId,
          zoneId: requestedZoneId,
        });
        setNotice(null);
      } else {
        setNotice({
          kind: "error",
          msg: `${ZONE_SCOPE_UNAVAILABLE_MESSAGE} ${ZONE_SCOPE_RECOVERY_MESSAGE}`,
        });
      }
      return { recover: false };
    }

    if (error?.code === "QUEUE_SCOPE_VERIFICATION_FAILED") {
      scopeLockSelectionRef.current = {
        venueId: requestedVenueId,
        zoneId: requestedZoneId,
      };
      setScopeLocked(true);
      setNotice({
        kind: "error",
        msg: "暫時無法驗證場域權限，操作已停用",
      });
      return { recover: false };
    }

    if (error?.status === 404 && error?.code === "VENUE_NOT_FOUND") {
      scopeLockSelectionRef.current = {
        venueId: requestedVenueId,
        zoneId: requestedZoneId,
      };
      setScopeLocked(true);
      setNotice({ kind: "error", msg: "無法存取此場域" });
      console.warn("[QueueOperations] venue access denied", {
        status: error.status,
        code: error.code,
        venue_id: requestedVenueId,
      });
      return { recover: false };
    }

    if (error?.status === 404) {
      setNotice({ kind: "error", msg: "找不到此號碼，可能已被其他操作員處理" });
      return { recover: true };
    }

    setNotice({
      kind: "error",
      msg: error?.status === 503
        ? "服務暫時無法使用，請稍後再試"
        : (error?.message || "操作失敗"),
    });
    return { recover: false };
  }, [expireAuth]);

  async function mutate(path, body = {}, successMsg = "操作完成") {
    if (
      !venueId
      || !sameSelection(loadedSelectionRef.current, venueId, zoneId)
      || commandLockRef.current
      || scopeLockSelectionRef.current
      || scopeLocked
      || zoneScopeUnavailable
      || authExpiredRef.current
    ) {
      return null;
    }

    commandLockRef.current = true;
    setCommandBusy(true);
    invalidateStateRequests();
    setSyncWarning(null);
    const commandVenueId = venueId;
    const commandZoneId = zoneId;
    const payload = { venue_id: commandVenueId, ...body };
    if (commandZoneId) payload.zone_id = commandZoneId;
    const idempotencyKey = createIdempotencyKey();

    try {
      const json = await api(`/api/v1/queue-console${path}`, {
        method: "POST",
        headers: { "X-Idempotency-Key": idempotencyKey },
        body: payload,
      });
      if (!mountedRef.current || authExpiredRef.current) return null;

      setNotice({ kind: "success", msg: successMsg });
      const needsFullRecovery = json.state_refresh_required === true || !json.state;
      if (json.state) {
        try {
          validateQueueStateScope(json.state, commandVenueId, commandZoneId || null);
          setScopedState(json.state);
          if (!commandZoneId) setVenueState(json.state);
        } catch (error) {
          lockStateScopeMismatch(error, commandVenueId, commandZoneId);
          setNotice({ kind: "success", msg: successMsg });
          setSyncWarning({
            kind: "warning",
            msg: "系統回傳的場域範圍不一致，操作已停用",
          });
          return json;
        }
      }

      if (needsFullRecovery) {
        setSyncWarning({ kind: "info", msg: "操作已成功，正在同步最新狀態" });
        try {
          const recovered = await loadAuthoritativeState({
            requestedVenueId: commandVenueId,
            requestedZoneId: commandZoneId,
            mode: "command-recovery",
          });
          if (recovered && mountedRef.current) setSyncWarning(null);
        } catch (error) {
          if (error?.code === "QUEUE_STATE_SCOPE_MISMATCH") {
            setNotice({ kind: "success", msg: successMsg });
            setSyncWarning({
              kind: "warning",
              msg: "系統回傳的場域範圍不一致，操作已停用",
            });
          } else if (error?.name !== "AbortError" && error?.status !== 401) {
            setSyncWarning({ kind: "warning", msg: "操作已成功，但暫時無法同步最新狀態" });
          }
        }
      } else if (commandZoneId) {
        try {
          const recovered = await loadAuthoritativeState({
            requestedVenueId: commandVenueId,
            requestedZoneId: commandZoneId,
            mode: "command-recovery",
            venueOnly: true,
          });
          if (recovered && mountedRef.current) setSyncWarning(null);
        } catch (error) {
          if (error?.code === "QUEUE_STATE_SCOPE_MISMATCH") {
            setNotice({ kind: "success", msg: successMsg });
            setSyncWarning({
              kind: "warning",
              msg: "系統回傳的場域範圍不一致，操作已停用",
            });
          } else if (error?.name !== "AbortError" && error?.status !== 401) {
            setSyncWarning({ kind: "warning", msg: "操作已成功，但暫時無法同步場域狀態" });
          }
        }
      }
      return json;
    } catch (error) {
      const policy = queueErrorPolicy(error, commandVenueId, commandZoneId);
      if (policy.recover && !authExpiredRef.current) {
        try {
          await loadAuthoritativeState({
            requestedVenueId: commandVenueId,
            requestedZoneId: commandZoneId,
            mode: "command-recovery",
          });
        } catch (_) {}
      }
      return null;
    } finally {
      commandLockRef.current = false;
      if (mountedRef.current) setCommandBusy(false);
    }
  }

  function mutateTicket(action, ticket, successMsg) {
    return mutate(`/actions/${action}`, { ticket_id: ticket.id }, successMsg);
  }

  async function createTicket() {
    const body = {};
    if (displayNumber.trim()) body.display_number = displayNumber.trim();
    const json = await mutate("/tickets", body, "號碼已建立");
    if (json) setDisplayNumber("");
  }

  function handleVenueChange(event) {
    if (commandLockRef.current) return;
    const nextVenueId = event.target.value;
    invalidateStateRequests();
    markStateUnready();
    setZoneScopeUnavailableSelection(null);
    setLastRefreshedAt(null);
    setManualRefreshError(null);
    selectionRef.current = { venueId: nextVenueId, zoneId: "" };
    setVenueState(null);
    setScopedState(null);
    setZones([]);
    setZoneId("");
    setVenueId(nextVenueId);
  }

  function handleZoneChange(event) {
    if (commandLockRef.current) return;
    const nextZoneId = event.target.value;
    invalidateStateRequests();
    markStateUnready();
    setZoneScopeUnavailableSelection(null);
    setLastRefreshedAt(null);
    setManualRefreshError(null);
    selectionRef.current = { venueId, zoneId: nextZoneId };
    setScopedState(null);
    setZoneId(nextZoneId);
  }

  function switchToVenueMode() {
    if (commandLockRef.current || authExpiredRef.current || !venueId) return;
    invalidateStateRequests();
    markStateUnready();
    setZoneScopeUnavailableSelection(null);
    setLastRefreshedAt(null);
    setManualRefreshError(null);
    selectionRef.current = { venueId, zoneId: "" };
    setScopedState(null);
    setZoneId("");
  }

  async function manualRefresh() {
    if (
      commandLockRef.current
      || manualRefreshBusyRef.current
      || authExpiredRef.current
      || !venueId
    ) return;
    manualRefreshBusyRef.current = true;
    setManualRefreshBusy(true);
    setLastRefreshedAt(null);
    setManualRefreshError(null);
    const requestedVenueId = venueId;
    const requestedZoneId = zoneId;
    invalidateStateRequests();
    try {
      const refreshed = await loadAuthoritativeState({
        requestedVenueId,
        requestedZoneId,
        mode: "manual",
      });
      if (
        refreshed
        && mountedRef.current
        && !authExpiredRef.current
        && sameSelection(selectionRef.current, requestedVenueId, requestedZoneId)
      ) {
        setLastRefreshedAt(new Date());
      }
    } catch (error) {
      if (
        error?.name !== "AbortError"
        && error?.status !== 401
        && mountedRef.current
        && !authExpiredRef.current
        && sameSelection(selectionRef.current, requestedVenueId, requestedZoneId)
      ) {
        setManualRefreshError("更新失敗，已保留目前畫面");
      }
    } finally {
      manualRefreshBusyRef.current = false;
      if (mountedRef.current) setManualRefreshBusy(false);
    }
  }

  const refreshDisabled = commandBusy || manualRefreshBusy || authExpired || !venueId;

  return (
    <div style={{ minWidth: 0 }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", flexWrap: "wrap", gap: 16, marginBottom: 20 }}>
        <div>
          <h2 style={{ margin: 0, fontSize: 22, color: t.text }}>櫃台叫號作業</h2>
          <div style={{ marginTop: 4, fontSize: 13, color: t.muted }}>Queue Operations v1</div>
        </div>
        <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-end", gap: 5 }}>
          <button
            onClick={manualRefresh}
            disabled={refreshDisabled}
            style={buttonStyle("secondary", refreshDisabled)}
          >
            {manualRefreshBusy ? "更新中…" : "重新整理"}
          </button>
          <div
            aria-live="polite"
            style={{
              minHeight: 18,
              fontSize: 12,
              color: manualRefreshError ? t.danger : t.muted,
            }}
          >
            {manualRefreshError || (
              lastRefreshedAt ? `已更新 ${fmtRefreshTime(lastRefreshedAt)}` : ""
            )}
          </div>
        </div>
      </div>

      {notice && (
        <div style={{
          marginBottom: 12,
          padding: "10px 12px",
          borderRadius: 8,
          border: `1px solid ${notice.kind === "error" ? t.danger : t.success}`,
          color: notice.kind === "error" ? t.danger : t.success,
          background: notice.kind === "error" ? t.dangerSoft : t.successSoft,
          fontSize: 13,
        }}>
          {notice.msg}
        </div>
      )}
      {syncWarning && (
        <div style={{ marginBottom: 12, padding: "10px 12px", borderRadius: 8, border: `1px solid ${t.warning}`, color: t.warning, background: t.warningSoft, fontSize: 13 }}>
          {syncWarning.msg}
        </div>
      )}
      {currentOwnedByOtherZone && (
        <div style={{ marginBottom: 12, padding: "10px 12px", borderRadius: 8, border: `1px solid ${t.warning}`, color: t.warning, background: t.warningSoft, fontSize: 13 }}>
          本場域目前由「{currentZoneLabel}」叫號 {current.display_number || current.ticket_number}
        </div>
      )}
      {venueScopeCurrent && (
        <div style={{ marginBottom: 12, padding: "10px 12px", borderRadius: 8, border: `1px solid ${t.warning}`, color: t.warning, background: t.warningSoft, fontSize: 13 }}>
          此場域目前使用場域級佇列，正在叫號 {current.display_number || current.ticket_number}
        </div>
      )}

      <Panel style={{ marginBottom: 16 }}>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))", gap: 12 }}>
          <Field label="場域">
            <select value={venueId} onChange={handleVenueChange} disabled={commandBusy || authExpired} style={inputStyle}>
              {venues.length === 0 && <option value="">尚無場域</option>}
              {venues.map((venue) => <option key={venue.id} value={venue.id}>{venue.name || venue.id}</option>)}
            </select>
          </Field>
          <Field label="區域">
            <select value={zoneId} onChange={handleZoneChange} disabled={commandBusy || authExpired || !venueId} style={inputStyle}>
              <option value="">全部區域 / 場域</option>
              {zones.map((zone) => <option key={zone.id} value={zone.id}>{zone.name || zone.id}</option>)}
            </select>
          </Field>
        </div>
        {selectedVenue && (
          <div style={{ marginTop: 10, fontSize: 12, color: t.dim }}>
            {selectedVenue.name || "目前場域"}{zoneId ? ` · ${zones.find((zone) => zone.id === zoneId)?.name || zoneId}` : ""}
          </div>
        )}
        {zoneScopeUnavailable && (
          <div style={{ marginTop: 10, padding: "10px 12px", borderRadius: 8, background: t.warningSoft, color: t.warning, fontSize: 12 }}>
            <div>{ZONE_SCOPE_UNAVAILABLE_MESSAGE}</div>
            <div style={{ marginTop: 4 }}>{ZONE_SCOPE_RECOVERY_MESSAGE}</div>
            <button
              onClick={switchToVenueMode}
              disabled={commandBusy || authExpired}
              style={{ ...buttonStyle("secondary", commandBusy || authExpired), marginTop: 10 }}
            >
              切換至場域模式
            </button>
          </div>
        )}
        {scopedState?.limitations?.length > 0 && !scopedStateHasZoneLimitation && (
          <div style={{ marginTop: 10, padding: "8px 10px", borderRadius: 8, background: t.warningSoft, color: t.warning, fontSize: 12 }}>
            此區域目前有範圍限制，操作前請確認服務狀態。
          </div>
        )}
      </Panel>

      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))", gap: 16, marginBottom: 16 }}>
        <Panel>
          <div style={{ fontSize: 13, color: t.muted, marginBottom: 8 }}>目前叫號</div>
          <div style={{ fontSize: 48, lineHeight: 1, fontWeight: 900, color: current ? t.warning : t.dim, fontFamily: t.mono }}>
            {current?.display_number || current?.ticket_number || "--"}
          </div>
          <div style={{ marginTop: 8, fontSize: 12, color: t.dim }}>
            {!stateReady
              ? "狀態尚未完成同步"
              : current
                ? `${statusLabel[current.status] || "處理中"} · 叫號時間 ${calledAt}`
                : "尚未叫號"}
          </div>
        </Panel>

        <Panel>
          <div style={{ fontSize: 13, color: t.muted, marginBottom: 12 }}>建立號碼</div>
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
            <input
              aria-label="display-number"
              value={displayNumber}
              onChange={(event) => setDisplayNumber(event.target.value)}
              placeholder="自動編號，或輸入 A12"
              disabled={mutationDisabled}
              style={{ ...inputStyle, flex: "1 1 180px" }}
            />
            <button onClick={createTicket} disabled={mutationDisabled} style={buttonStyle("primary", mutationDisabled)}>建立</button>
          </div>
        </Panel>
      </div>

      <Panel style={{ marginBottom: 16 }}>
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
          <button
            onClick={() => mutate("/actions/call-next", {}, "已叫下一號")}
            disabled={mutationDisabled || !!current}
            style={buttonStyle("success", mutationDisabled || !!current)}
          >
            呼叫下一位
          </button>
          <button
            onClick={() => mutate("/actions/recall", {}, "已再次叫號")}
            disabled={mutationDisabled || !currentOwnedByScope}
            style={buttonStyle("secondary", mutationDisabled || !currentOwnedByScope)}
          >
            再次叫號
          </button>
          <button
            onClick={() => mutate("/actions/hold", {}, "目前號碼已保留")}
            disabled={mutationDisabled || !currentOwnedByScope}
            style={buttonStyle("warning", mutationDisabled || !currentOwnedByScope)}
          >
            保留
          </button>
          <button
            onClick={() => mutate("/actions/skip", {}, "目前號碼已過號")}
            disabled={mutationDisabled || !currentOwnedByScope}
            style={buttonStyle("danger", mutationDisabled || !currentOwnedByScope)}
          >
            過號
          </button>
          <button
            onClick={() => mutate("/actions/complete", {}, "目前號碼已完成")}
            disabled={mutationDisabled || !currentOwnedByScope}
            style={buttonStyle("success", mutationDisabled || !currentOwnedByScope)}
          >
            完成
          </button>
        </div>
      </Panel>

      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(300px, 1fr))", gap: 16 }}>
        <div style={{ minWidth: 0 }}>
          <Panel>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
              <div style={{ fontSize: 14, fontWeight: 800, color: t.text }}>等候中</div>
              <div style={{ fontSize: 12, color: t.muted }}>{scopedState?.waiting_tickets?.length || 0} 組</div>
            </div>
            {!stateReady && !scopedState?.waiting_tickets?.length ? (
              <div style={{ padding: 24, textAlign: "center", color: t.muted }}>
                {loading ? "載入中..." : "狀態尚未完成同步"}
              </div>
            ) : scopedState?.waiting_tickets?.length ? (
              scopedState.waiting_tickets.map((ticket) => (
                <WaitingTicketRow
                  key={ticket.id}
                  ticket={ticket}
                  disabled={mutationDisabled}
                  callDisabled={!!current}
                  onAction={mutateTicket}
                />
              ))
            ) : (
              <div style={{ padding: 24, textAlign: "center", color: t.muted, border: `1px dashed ${t.border}`, borderRadius: 8 }}>
                目前無等候號碼
              </div>
            )}
          </Panel>

          <Panel style={{ marginTop: 16 }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
              <div style={{ fontSize: 14, fontWeight: 800, color: t.text }}>保留中</div>
              <div style={{ fontSize: 12, color: t.muted }}>{scopedState?.held_tickets?.length || 0} 組</div>
            </div>
            {!stateReady && !scopedState?.held_tickets?.length ? (
              <div style={{ padding: 24, textAlign: "center", color: t.muted }}>
                {loading ? "載入中..." : "狀態尚未完成同步"}
              </div>
            ) : scopedState?.held_tickets?.length ? (
              scopedState.held_tickets.map((ticket) => (
                <HeldTicketRow
                  key={ticket.id}
                  ticket={ticket}
                  disabled={mutationDisabled}
                  callDisabled={!!current}
                  onAction={mutateTicket}
                />
              ))
            ) : (
              <div style={{ padding: 24, textAlign: "center", color: t.muted, border: `1px dashed ${t.border}`, borderRadius: 8 }}>
                目前無保留號碼
              </div>
            )}
          </Panel>
        </div>

        <Panel>
          <div style={{ fontSize: 14, fontWeight: 800, color: t.text, marginBottom: 12 }}>最近操作</div>
          <div style={{ maxHeight: 360, overflowY: "auto" }}>
            {scopedState?.recent_events?.length ? scopedState.recent_events.map((event, index) => {
              const display = typeof event.metadata === "object" && typeof event.metadata?.display_number === "string"
                ? event.metadata.display_number
                : null;
              return (
                <div key={event.id || `${event.created_at}-${index}`} style={{ padding: "9px 0", borderBottom: `1px solid ${t.border}` }}>
                  <div style={{ display: "flex", justifyContent: "space-between", flexWrap: "wrap", gap: 8 }}>
                    <span style={{ fontSize: 12, color: t.text, fontWeight: 700 }}>
                      {event.actor_label || "操作員"} · {eventLabel[event.action] || "系統操作"}{display ? ` · ${display}` : ""}
                    </span>
                    <span style={{ fontSize: 11, color: t.dim }}>{fmtTime(event.created_at)}</span>
                  </div>
                </div>
              );
            }) : (
              <div style={{ padding: 18, color: t.muted, textAlign: "center", fontSize: 13 }}>尚無操作紀錄</div>
            )}
          </div>
        </Panel>
      </div>
    </div>
  );
}
